/**
 * Job status state machine — the single source of truth shared by api/web/pwa.
 * Derived directly from the PRD "Job Status Model" table.
 */
import { JOB_STATUSES } from './constants.js';
export { JOB_STATUSES };
/**
 * Legal transitions (PRD table):
 *   Received -> Unallocated | Rejected
 *   Unallocated -> Allocated | Cancelled
 *   Allocated -> Accepted | Unallocated (release)
 *   Accepted -> In Progress
 *   In Progress -> Completed | Exception
 *   Exception -> In Progress | Cancelled
 *   Completed -> Invoice Draft
 *   Invoice Draft -> Invoice Sent
 *   Invoice Sent -> Payment Completed | Void
 *   Payment Completed -> (terminal)
 */
export const JOB_TRANSITIONS = [
    { from: 'received', to: 'unallocated', roles: ['ADMIN', 'INTEGRATION'], automated: true },
    { from: 'received', to: 'rejected', roles: ['ADMIN', 'INTEGRATION'] },
    { from: 'unallocated', to: 'allocated', roles: ['ADMIN'] },
    { from: 'unallocated', to: 'cancelled', roles: ['ADMIN'] },
    { from: 'allocated', to: 'accepted', roles: ['DRIVER', 'ADMIN'] },
    { from: 'allocated', to: 'unallocated', roles: ['ADMIN'] },
    { from: 'accepted', to: 'in_progress', roles: ['DRIVER', 'ADMIN'] },
    { from: 'in_progress', to: 'completed', roles: ['DRIVER', 'ADMIN'] },
    { from: 'in_progress', to: 'exception', roles: ['DRIVER', 'ADMIN'] },
    { from: 'exception', to: 'in_progress', roles: ['DRIVER', 'ADMIN'] },
    { from: 'exception', to: 'cancelled', roles: ['ADMIN'] },
    { from: 'completed', to: 'invoice_draft', roles: ['ADMIN', 'FINANCE'], automated: true },
    { from: 'invoice_draft', to: 'invoice_sent', roles: ['ADMIN', 'FINANCE'] },
    { from: 'invoice_sent', to: 'payment_completed', roles: ['ADMIN', 'FINANCE'] },
    { from: 'invoice_sent', to: 'void', roles: ['ADMIN', 'FINANCE'] },
    { from: 'invoice_draft', to: 'void', roles: ['ADMIN', 'FINANCE'] },
];
/** transition map: from -> allowed destinations */
export const JOB_TRANSITION_MAP = Object.freeze(JOB_STATUSES.reduce((acc, status) => {
    acc[status] = Object.freeze(JOB_TRANSITIONS.filter((t) => t.from === status).map((t) => t.to));
    return acc;
}, {}));
export const TERMINAL_STATUSES = JOB_STATUSES.filter((s) => (JOB_TRANSITION_MAP[s] ?? []).length === 0);
export function isTerminalStatus(status) {
    return TERMINAL_STATUSES.includes(status);
}
export function isJobStatus(value) {
    return typeof value === 'string' && JOB_STATUSES.includes(value);
}
/** Can the job move from -> to? Role check is optional (structural check when omitted). */
export function canTransition(from, to, role) {
    const rule = JOB_TRANSITIONS.find((t) => t.from === from && t.to === to);
    if (!rule)
        return false;
    if (role !== undefined && !rule.roles.includes(role))
        return false;
    return true;
}
export function getTransitionRule(from, to) {
    return JOB_TRANSITIONS.find((t) => t.from === from && t.to === to);
}
/** Roles that may perform a given transition. */
export function rolesForTransition(from, to) {
    return getTransitionRule(from, to)?.roles ?? [];
}
/** Outgoing transitions available to a role (used to render action buttons). */
export function allowedNextStatuses(from, role) {
    return JOB_TRANSITIONS.filter((t) => t.from === from && (role === undefined || t.roles.includes(role))).map((t) => t.to);
}
export class InvalidTransitionError extends Error {
    from;
    to;
    code = 'INVALID_TRANSITION';
    constructor(from, to, role) {
        super(role === undefined
            ? `Illegal job transition ${from} -> ${to}`
            : `Illegal job transition ${from} -> ${to} for role ${role}`);
        this.from = from;
        this.to = to;
        this.name = 'InvalidTransitionError';
    }
}
/** Throws InvalidTransitionError when the transition is not permitted. */
export function assertTransition(from, to, role) {
    if (!canTransition(from, to, role))
        throw new InvalidTransitionError(from, to, role);
    return getTransitionRule(from, to);
}
//# sourceMappingURL=job-status.js.map